import type { FC } from 'react';
import React, { useCallback, memo } from 'react';
import type { TInputType } from '@alfa-bank/site-input/models';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import type { TValidatorValue } from '@alfa-bank/site-form-helpers/src/validation/models';
import { validateNotEmpty } from '@alfa-bank/site-form-helpers/validation/not-empty';
import { composeValidators } from '@alfa-bank/site-form-helpers/validation/utils';

import type { TFormBirthday } from './models';
import { DEFAULT_SEPARATOR } from './config';
import { validateBirthdayValue } from './helpers';

/**
 * Инпут даты рождения
 * memo требуется для того чтоб избежать лишнего
 * ререндера при сабмите, при котором Final Form сбрасывает значения
 * meta.touched, из-за чего пропадают галочки напротив
 * корректно заполненных полей
 */
export const FormBirthday: FC<TFormBirthday> = memo(
    ({
        BirthdayField,
        Field,
        className,
        errorMessage,
        ageLimit,
        ageCeil,
        getCaption,
        isNextFocus = true,
        isError,
        isSuccess,
        hideStatusIcon,
        name,
        dataTestId,
        settings = {},
        validateBirthday,
        customValidator,
        isRequired = true, // для обратной совместимости
    }) => {
        const validate = useCallback(() => {
            const validators: Array<(value: string, allValues: object) => TValidatorValue> = [];

            if (isRequired) {
                validators.push(validateNotEmpty());
            }

            // Применяется всегда, но проверки производятся только если переданы ageLimit или ageCeil
            validators.push(validateBirthdayValue(errorMessage, ageLimit, ageCeil));

            if (customValidator) {
                validators.push(customValidator());
            }

            return composeValidators(...validators);
        }, [ageCeil, ageLimit, customValidator, errorMessage, isRequired]);

        return (
            <Field
                data={settings}
                dispatchesFocus
                name={name}
                receivesFocus
                render={({ input, meta }) => {
                    const { name: inputName, type, value: inputValue, ...restInput } = input;
                    const { withAdditionalZero = true } = settings;

                    return (
                        <div className={className}>
                            <BirthdayField
                                {...restInput}
                                caption={getCaption(meta, settings.caption, inputValue)}
                                dataTestId={dataTestId}
                                error={isError(meta, inputValue)}
                                forwardedRef={actions.getRef(meta)}
                                hideStatusIcon={hideStatusIcon}
                                name="passportBirthDateField"
                                onBlur={actions.onBlur(input)}
                                onCancel={actions.onCancel({ input, meta })}
                                onChange={value => {
                                    actions.onChange(input)(value);
                                    if (isNextFocus && !validateBirthday()(value))
                                        actions.onComplete({ input, meta });
                                }}
                                onFocus={actions.onFocus(input)}
                                placeholder={settings.placeholder}
                                separator={settings.separator || DEFAULT_SEPARATOR}
                                success={isSuccess(meta)}
                                type={type as TInputType}
                                value={inputValue}
                            />
                            <input
                                name={inputName}
                                type="hidden"
                                value={
                                    withAdditionalZero
                                        ? input.value
                                        : input.value.replace(/\b0/g, '')
                                }
                            />
                        </div>
                    );
                }}
                validate={validate()}
            />
        );
    }
);
